import React, { useState, useRef, useLayoutEffect } from 'react';
import styled from 'styled-components';
import { motion, useAnimation } from 'framer-motion';
import { Reply } from '@styled-icons/fa-solid/Reply';
import { getAuth } from 'firebase/auth';
import { toast } from 'react-toastify';
import { API_URL } from '../../../constants/string-constants';
import DMMessageBubble from './DMMessageBubble';
import DMInputBar from './DMInputBar';
import MessageActionsMenu from '../MessageActionsMenu';
import ReportMessageModal from '../ReportMessageModal';

const ChatContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  min-height: 0; /* Allows flex item to shrink below its content size */

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: var(--clr-bg-secondary, #2a2a2a);
  }

  &::-webkit-scrollbar-thumb {
    background: var(--clr-border, #444);
    border-radius: 2px;
  }
`;

const InputContainer = styled.div`
  padding: 12px;
  border-top: 1px solid var(--clr-border, #444);
  flex-shrink: 0;
  background-color: var(--clr-bg-secondary, #2a2a2a);
  position: relative;
  z-index: 10;
`;

const MessageContainer = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const SwipeableMessageWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const ReplyIconContainer = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 8px;
  opacity: ${(props) => props.$opacity};
  transition: opacity 0.15s ease;
  pointer-events: none;
  color: var(--clr-text-secondary, #aaa);
  z-index: 0;
`;

const ReplyIcon = styled(Reply)`
  width: 18px;
  height: 18px;
`;

// Static objects to avoid creating new references on every render
const swipeDragConstraints = { left: 0, right: 80 };
const swipeStyle = { width: '100%', touchAction: 'pan-y' };

// Swipe-to-reply wrapper, mirroring SwipeableMessage in room chat (MessagesContainer.jsx).
// Only non-self messages can be swiped to reply.
const SwipeableDMMessage = ({ message, isFromMe, children, onReply }) => {
  const [showReplyIcon, setShowReplyIcon] = useState(false);
  const controls = useAnimation();

  return (
    <SwipeableMessageWrapper>
      <ReplyIconContainer $opacity={showReplyIcon ? 1 : 0}>
        <ReplyIcon />
      </ReplyIconContainer>

      <motion.div
        animate={controls}
        drag={isFromMe ? false : 'x'}
        dragConstraints={swipeDragConstraints}
        dragElastic={0.2}
        dragMomentum={false}
        onDrag={(event, info) => {
          setShowReplyIcon(info.offset.x > 30);
        }}
        onDragEnd={async (event, info) => {
          if (info.offset.x > 50 && !isFromMe) {
            onReply(message);
          }
          setShowReplyIcon(false);
          await controls.start({
            x: 0,
            transition: { type: 'spring', damping: 40, stiffness: 400 },
          });
        }}
        style={swipeStyle}
      >
        {children}
      </motion.div>
    </SwipeableMessageWrapper>
  );
};

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: var(--clr-text-secondary, #aaa);
`;

const EmptyState = styled.div`
  padding: 32px 16px;
  text-align: center;
  color: var(--clr-text-secondary, #aaa);
`;

const EmptyTitle = styled.h4`
  margin: 0 0 8px 0;
  color: var(--clr-text, #ffffff);
`;

const EmptyText = styled.p`
  margin: 0;
  line-height: 1.4;
`;

function DMChatView({
  chatMessages,
  loadingMessages,
  chatType,
  activeChatConversation,
  activeGroupConversation,
  onSendMessage
}) {
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [messageToReport, setMessageToReport] = useState(null);
  const [hoveredMessageId, setHoveredMessageId] = useState(null);
  const [replyToMessage, setReplyToMessage] = useState(null);

  const messagesContainerRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Begin replying to a message (from swipe or actions menu).
  // Clear first so re-replying to the same message still re-triggers the input focus effect.
  const handleReply = (message) => {
    setReplyToMessage(null);
    setTimeout(() => setReplyToMessage(message), 0);
  };

  const handleClearReply = () => setReplyToMessage(null);

  // Scroll to and briefly highlight the original message when a reply preview is clicked.
  const handleReplyClick = (replyToId) => {
    const target = messagesContainerRef.current?.querySelector(
      `[data-message-id="${replyToId}"]`
    );
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      target.style.transition = 'background-color 0.3s ease';
      target.style.backgroundColor = 'rgba(0, 123, 255, 0.15)';
      setTimeout(() => {
        target.style.backgroundColor = '';
      }, 1200);
    } else {
      toast.info('Original message not loaded');
    }
  };

  // Wrap onSendMessage so the reply banner clears once a message is sent
  const handleSendMessage = (messageData) => {
    onSendMessage(messageData);
    setReplyToMessage(null);
  };

  // Auto-scroll to bottom when messages finish loading or new messages render
  useLayoutEffect(() => {
    if (!loadingMessages && chatMessages.length > 0) {
      const container = messagesContainerRef.current;
      if (container) {
        const maxScrollTop = container.scrollHeight - container.clientHeight;
        container.scrollTo({ top: maxScrollTop, behavior: 'auto' });
      }
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({
          behavior: 'auto',
          block: 'end',
        });
      }
      // Fallback after next paint
      requestAnimationFrame(() => {
        const container2 = messagesContainerRef.current;
        if (container2) {
          const maxScrollTop2 =
            container2.scrollHeight - container2.clientHeight;
          container2.scrollTo({ top: maxScrollTop2, behavior: 'auto' });
        }
      });
    }
  }, [loadingMessages, chatMessages]);

  const handleReportMessage = (message) => {
    setMessageToReport(message);
    setReportModalOpen(true);
  };

  const handleCloseReportModal = () => {
    setReportModalOpen(false);
    setMessageToReport(null);
  };

  const handleBlockUser = async (message) => {
    if (chatType !== 'dm' || !activeChatConversation?.friend?.id) {
      toast.error('Unable to block user');
      return;
    }

    const friendId = activeChatConversation.friend.id;
    const auth = getAuth();

    if (!auth.currentUser) {
      toast.error('Please sign in to block users');
      return;
    }

    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(API_URL + `friends/remove/${friendId}`, {
        method: 'DELETE',
        headers: {
          Authorization: 'Bearer ' + token,
        },
      });

      if (response.ok) {
        toast.success('User blocked (friend removed) successfully!');
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Failed to block user');
      }
    } catch (error) {
      console.error('Error blocking user:', error);
      toast.error('Failed to block user');
    }
  };

  const getCurrentUserId = () => {
    const auth = getAuth();
    return auth.currentUser?.uid || null;
  };

  return (
    <ChatContainer>
      <MessagesContainer ref={messagesContainerRef}>
        {loadingMessages && (
          <LoadingContainer>Loading messages...</LoadingContainer>
        )}

        {!loadingMessages && chatMessages.length === 0 && (
          <EmptyState>
            <EmptyTitle>Start the conversation</EmptyTitle>
            <EmptyText>
              {chatType === 'dm' 
                ? `Send your first message to ${activeChatConversation?.friend?.nickname || 'Anon'}`
                : `Send your first message to ${activeGroupConversation?.name || 'the group'}`
              }
            </EmptyText>
          </EmptyState>
        )}

        {!loadingMessages &&
          chatMessages.map((message, index, array) => {
            // Determine if we should show timestamp for this message
            // We show timestamp only on the last message of a group from the same sender
            
            const nextMessage = index < array.length - 1 ? array[index + 1] : null;
            let showTimestamp = false;
            
            if (!nextMessage) {
              // This is the last message in the array, always show timestamp
              showTimestamp = true;
            } else {
              // Check if the next message is from a different user
              // For group messages, check sender.id; for DMs, check isFromMe
              const differentSender = chatType === 'group' 
                ? (message.sender?.id !== nextMessage.sender?.id)
                : (message.isFromMe !== nextMessage.isFromMe);
              
              // Check if there's a time gap to the next message
              const currentTime = new Date(message.createdAt).getTime();
              const nextTime = new Date(nextMessage.createdAt).getTime();
              const timeGap = nextTime - currentTime; // Time until next message
              const hasTimeGap = timeGap > 60000; // More than 1 minute gap
              
              // Show timestamp if sender changes OR there's a time gap
              showTimestamp = differentSender || hasTimeGap;
            }
            
            return (
              <MessageContainer
                key={message.id}
                data-message-id={message.id}
                onMouseEnter={() => setHoveredMessageId(message.id)}
                onMouseLeave={() => setHoveredMessageId(null)}
              >
                <div data-chat-message style={{ flex: 1 }}>
                  <SwipeableDMMessage
                    message={message}
                    isFromMe={message.isFromMe}
                    onReply={handleReply}
                  >
                    <DMMessageBubble
                      message={message}
                      isFromMe={message.isFromMe}
                      friendColor={
                        activeChatConversation?.friend?.custom_color ||
                        activeChatConversation?.friend?.assigned_color
                      }
                      friendNickname={activeChatConversation?.friend?.nickname}
                      showTimestamp={showTimestamp}
                      onReplyClick={handleReplyClick}
                    />
                  </SwipeableDMMessage>
                </div>

                {/* Only show actions menu for non-self messages (report + reply) */}
                {!message.isFromMe && (
                  <MessageActionsMenu
                    message={message}
                    isMod={false}
                    onReport={handleReportMessage}
                    onReply={handleReply}
                    currentUserId={getCurrentUserId()}
                    isHovered={hoveredMessageId === message.id}
                    // Leave the rest undefined to hide them
                    onFriendRequest={undefined}
                    onBlock={undefined}
                    onBan={undefined}
                    onViewProfile={undefined}
                    onAddReaction={undefined}
                    onRemoveReaction={undefined}
                  />
                )}
              </MessageContainer>
            );
          })}
        <div ref={messagesEndRef} style={{ height: 1 }} />
      </MessagesContainer>

      <InputContainer>
        <DMInputBar
          onSendMessage={handleSendMessage}
          disabled={loadingMessages}
          replyToMessage={replyToMessage}
          onClearReply={handleClearReply}
        />
      </InputContainer>

      {/* Report Message Modal */}
      {reportModalOpen && messageToReport && (
        <ReportMessageModal
          isOpen={reportModalOpen}
          onClose={handleCloseReportModal}
          message={messageToReport}
          room={chatType === 'dm' ? activeChatConversation?.id : activeGroupConversation?.id}
          messageType={chatType}
          onBlock={handleBlockUser}
        />
      )}
    </ChatContainer>
  );
}

export default DMChatView;